import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    ArrowLeft, 
    Save, 
    Loader2,
    Lightbulb,
    PiggyBank,
    ShoppingCart,
    Archive,
    ChefHat
} from 'lucide-react';
import { toast } from 'sonner';

export const SubmitTipPage = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'budget',
        savings_estimate: ''
    });

    const categories = [
        { value: 'budget', label: 'Budget', icon: PiggyBank, color: 'text-green-500' },
        { value: 'shopping', label: 'Courses', icon: ShoppingCart, color: 'text-blue-500' },
        { value: 'storage', label: 'Conservation', icon: Archive, color: 'text-purple-500' },
        { value: 'cooking', label: 'Cuisine', icon: ChefHat, color: 'text-orange-500' }
    ];

    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
        }
    }, [user, loading, navigate]);

    // Show loading while checking auth
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.title || !formData.content) {
            toast.error('Veuillez remplir le titre et le contenu');
            return;
        }
        
        if (formData.title.length < 5) {
            toast.error('Le titre doit contenir au moins 5 caractères');
            return;
        }
        
        if (formData.content.length < 20) {
            toast.error('Le contenu doit contenir au moins 20 caractères');
            return;
        }
        
        setSaving(true);
        try {
            await api.createTip(formData);
            toast.success('Astuce soumise avec succès! Elle sera visible après modération.');
            navigate('/tips');
        } catch (error) {
            console.error('Error saving tip:', error);
            toast.error('Erreur lors de la soumission');
        } finally {
            setSaving(false);
        }
    };

    const selectedCategory = categories.find(c => c.value === formData.category);

    return (
        <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-20 -right-40 opacity-20" />
            <div className="blob-purple bottom-40 -left-40 opacity-20" />
            
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour
                    </Button>
                    
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center border-2 border-foreground shadow-pop">
                            <Lightbulb className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                                Proposer une astuce
                            </h1>
                            <p className="text-muted-foreground">
                                Partagez vos secrets pour économiser!
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Moderation Notice */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3 mb-8">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">📝</span>
                    </div>
                    <div>
                        <p className="font-semibold text-amber-800">Soumission à modération</p>
                        <p className="text-sm text-amber-700">
                            Votre astuce sera examinée par notre équipe avant d&apos;être publiée. 
                            Merci de contribuer à la communauté!
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <Card className="rounded-2xl border-2 border-foreground/10 shadow-lg">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Lightbulb className="w-5 h-5 text-yellow-500" />
                                Votre astuce économique
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Title */}
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre de l&apos;astuce *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                    placeholder="Ex: Congeler le pain pour éviter le gaspillage"
                                    className="rounded-xl border-2"
                                    data-testid="tip-title-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Un titre accrocheur et descriptif (min. 5 caractères)
                                </p>
                            </div>
                            
                            {/* Category */}
                            <div className="space-y-2">
                                <Label>Catégorie</Label>
                                <Select value={formData.category} onValueChange={(v) => handleInputChange('category', v)}>
                                    <SelectTrigger className="rounded-xl border-2" data-testid="tip-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => {
                                            const IconComponent = cat.icon;
                                            return (
                                                <SelectItem key={cat.value} value={cat.value}>
                                                    <div className="flex items-center gap-2">
                                                        <IconComponent className={`w-4 h-4 ${cat.color}`} />
                                                        {cat.label}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                                
                                {/* Category preview */}
                                {selectedCategory && (
                                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm`}>
                                        <selectedCategory.icon className={`w-4 h-4 ${selectedCategory.color}`} />
                                        <span>{selectedCategory.label}</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Content */}
                            <div className="space-y-2">
                                <Label htmlFor="content">Contenu de l&apos;astuce *</Label>
                                <Textarea
                                    id="content"
                                    value={formData.content}
                                    onChange={(e) => handleInputChange('content', e.target.value)}
                                    placeholder="Expliquez votre astuce en détail. Comment ça marche? Pourquoi c'est économique? Des conseils pratiques..."
                                    className="rounded-xl border-2 min-h-[180px]"
                                    data-testid="tip-content-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Soyez précis et pratique (min. 20 caractères) - {formData.content.length} caractères
                                </p>
                            </div>
                            
                            {/* Savings Estimate */}
                            <div className="space-y-2">
                                <Label htmlFor="savings_estimate" className="flex items-center gap-2">
                                    <PiggyBank className="w-4 h-4 text-green-500" />
                                    Économies estimées (optionnel)
                                </Label>
                                <Input
                                    id="savings_estimate"
                                    value={formData.savings_estimate}
                                    onChange={(e) => handleInputChange('savings_estimate', e.target.value)}
                                    placeholder="Ex: 5-10$ par semaine, ~20% sur les fruits"
                                    className="rounded-xl border-2"
                                    data-testid="tip-savings-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Estimez combien cette astuce peut faire économiser
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tips for good submission */}
                    <Card className="rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
                        <CardContent className="pt-6">
                            <h3 className="font-heading font-bold text-green-800 mb-3 flex items-center gap-2">
                                <span>💡</span> Conseils pour une bonne astuce
                            </h3>
                            <ul className="space-y-2 text-sm text-green-700">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>Soyez <strong>spécifique</strong> - donnez des chiffres et des exemples concrets</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>Pensez <strong>Québec</strong> - mentionnez les magasins locaux (IGA, Maxi, Costco...)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>Expliquez le <strong>pourquoi</strong> - comment cette astuce fait économiser?</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">✓</span>
                                    <span>Partagez votre <strong>expérience</strong> - les astuces vécues sont les meilleures!</span>
                                </li>
                            </ul>
                        </CardContent>
                    </Card>

                    {/* Submit */}
                    <div className="flex gap-4 justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="rounded-xl border-2"
                        >
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            className="btn-pop bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl px-8"
                            data-testid="submit-tip-btn"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Envoi...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Soumettre l&apos;astuce
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SubmitTipPage;
